import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, LinkOff as LinkOffIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../hooks/useAuth';
import { getFriendlyErrorMessage } from '../../../utils/errorUtils';
import { ROLE_ADMINISTRATOR, ROLE_FINANCE, ROLE_SUPER_ADMIN } from '../../../constants/roles';
import {
  sageSalesRepService,
  salesRepCandidateFromError,
  previewSalesRepName,
  salesRepDisplayName,
} from '../../../services/sageSalesRepService';
import type { AgentSalesRepLink, SageSalesRep } from '../../../types/sage';

/** Roles behind the FinanceWrite policy that gates every rep endpoint. */
const SALES_REP_PANEL_ROLES = [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR, ROLE_FINANCE];

interface AgentSalesRepLinkPanelProps {
  agentId: number;
  agentName: string;
  /** The agent's primary email (sent to Sage on create; used to pre-highlight a matching rep). */
  primaryEmail?: string | null;
  /** The agent's additional emails (used to pre-highlight a matching rep). */
  additionalEmails?: string[];
  /** The agent's contact number (sent to Sage as the rep's mobile on create). */
  contactNumber?: string | null;
  /** Called after a successful link, unlink or create, so the caller can refresh what it shows. */
  onChanged?: () => void;
}

const errorMessage = (err: unknown, fallback: string) => getFriendlyErrorMessage(err, fallback);

/**
 * Links an agent to a Sage Sales Representative (DRI-85): the rep that goes on invoices of
 * workflows where this agent is the Sales Agent. Pick an existing rep, or create one in Sage from
 * the agent's details. Visible only to FinanceWrite users (SuperAdmin, Administrator, Finance).
 */
export const AgentSalesRepLinkPanel: React.FC<AgentSalesRepLinkPanelProps> = (props) => {
  const { hasAnyRole } = useAuth();
  if (!hasAnyRole(SALES_REP_PANEL_ROLES)) return null;
  return <AgentSalesRepLinkPanelInner {...props} />;
};

const AgentSalesRepLinkPanelInner: React.FC<AgentSalesRepLinkPanelProps> = ({
  agentId, agentName, primaryEmail, additionalEmails, contactNumber, onChanged,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [link, setLink] = useState<AgentSalesRepLink | null>(null);
  const [reps, setReps] = useState<SageSalesRep[]>([]);
  const [selected, setSelected] = useState<SageSalesRep | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [candidateNotice, setCandidateNotice] = useState<string | null>(null);
  // Synchronous guard: two clicks in the same tick both see the stale `busy` from one render.
  const creatingRef = useRef(false);

  const agentEmails = useMemo(
    () => new Set([primaryEmail, ...(additionalEmails ?? [])]
      .filter((e): e is string => !!e && !!e.trim())
      .map((e) => e.trim().toLowerCase())),
    [primaryEmail, additionalEmails],
  );

  const preHighlight = useCallback(
    (list: SageSalesRep[]) =>
      list.find((r) => r.active && !!r.email && agentEmails.has(r.email.trim().toLowerCase())) ?? null,
    [agentEmails],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [currentLink, repList] = await Promise.all([
          sageSalesRepService.getAgentLink(agentId),
          sageSalesRepService.getSalesReps(),
        ]);
        if (cancelled) return;
        setLink(currentLink);
        setReps(repList);
        if (!currentLink.sageSalesRepId) setSelected(preHighlight(repList));
      } catch (err) {
        if (!cancelled) setLoadError(errorMessage(err, 'Failed to load Sage Sales Reps'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, preHighlight]);

  const handleRefresh = async () => {
    setBusy(true);
    try {
      const repList = await sageSalesRepService.refreshSalesReps();
      setReps(repList);
      setSelected((current) => (current && repList.find((r) => r.id === current.id)) ?? preHighlight(repList));
      setLoadError(null);
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Failed to refresh Sage Sales Reps'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleLink = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      setLink(await sageSalesRepService.linkAgent(agentId, selected.id));
      setCandidateNotice(null);
      onChanged?.();
      enqueueSnackbar('Sales Rep linked', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Failed to link Sales Rep'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    setBusy(true);
    try {
      await sageSalesRepService.unlinkAgent(agentId);
      setLink({ agentId });
      setSelected(preHighlight(reps));
      onChanged?.();
      enqueueSnackbar('Sales Rep unlinked', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Failed to unlink Sales Rep'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    // Guard against a double click: a second create could make a second rep in Sage.
    if (busy || creatingRef.current) return;
    creatingRef.current = true;
    setBusy(true);
    setConfirmOpen(false);
    try {
      setLink(await sageSalesRepService.createForAgent(agentId));
      setCandidateNotice(null);
      onChanged?.();
      enqueueSnackbar('Sales Rep created in Sage and linked', { variant: 'success' });
      // The new rep is now in the (invalidated) server list; refresh ours quietly.
      sageSalesRepService.getSalesReps().then(setReps).catch(() => undefined);
    } catch (err) {
      const candidate = salesRepCandidateFromError(err);
      if (candidate) {
        let match = reps.find((r) => r.id === candidate.id);
        if (!match) {
          // The server refreshed its cache before matching; refresh ours so the candidate is listed.
          const repList = await sageSalesRepService.getSalesReps().catch(() => reps);
          setReps(repList);
          match = repList.find((r) => r.id === candidate.id);
        }
        setSelected(match ?? {
          id: candidate.id, firstName: candidate.name, lastName: '', name: candidate.name,
          email: candidate.email, active: candidate.active,
        });
        setCandidateNotice(candidate.active
          ? `A Sales Rep with the same ${candidate.matchedOn === 'Email' ? 'email' : 'name'} already exists in Sage `
            + `(${candidate.name || `#${candidate.id}`}). Link this one instead of creating a duplicate.`
          : `A matching rep exists (${candidate.name || `#${candidate.id}`}) but is inactive in Sage. `
            + 'Reactivate it in Sage, then refresh.');
      } else {
        enqueueSnackbar(errorMessage(err, 'Failed to create Sales Rep in Sage'), { variant: 'error' });
      }
    } finally {
      creatingRef.current = false;
      setBusy(false);
    }
  };

  const preview = previewSalesRepName(agentName);
  const linkedRep = link?.sageSalesRepId ? reps.find((r) => r.id === link.sageSalesRepId) : undefined;
  const linkedName = link?.salesRepName || (linkedRep ? salesRepDisplayName(linkedRep) : null);
  const linkedEmail = link?.salesRepEmail ?? linkedRep?.email;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="agent-sales-rep-panel">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Sage Sales Rep</Typography>
        <Tooltip title="Refresh the Sage Sales Rep list">
          <span>
            <IconButton size="small" onClick={handleRefresh} disabled={busy || loading} aria-label="Refresh Sales Reps">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Goes on the invoice of workflows where this agent is the Sales Agent.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={20} /></Box>
      ) : loadError ? (
        <Alert severity="error">{loadError}</Alert>
      ) : link?.sageSalesRepId ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" data-testid="linked-sales-rep">
              {linkedName ?? `Sales Rep #${link.sageSalesRepId}`}
            </Typography>
            {linkedEmail && <Typography variant="caption" color="text.secondary">{linkedEmail}</Typography>}
            {link.salesRepActive === false && (
              <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>Inactive in Sage</Typography>
            )}
          </Box>
          <Button size="small" startIcon={<LinkOffIcon />} onClick={handleUnlink} disabled={busy}>
            Unlink
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {candidateNotice && <Alert severity="info">{candidateNotice}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Autocomplete<SageSalesRep>
              fullWidth
              size="small"
              options={reps}
              value={selected}
              onChange={(_e, value) => setSelected(value)}
              getOptionLabel={salesRepDisplayName}
              getOptionDisabled={(r) => !r.active}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(optionProps, rep) => {
                const { key, ...rest } = optionProps as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                return (
                  <li key={key} {...rest}>
                    <Box>
                      <Typography variant="body2">
                        {salesRepDisplayName(rep)}{!rep.active && ' (inactive)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{rep.email || 'No email'}</Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No Sales Reps in Sage"
              disabled={busy}
              renderInput={(params) => <TextField {...params} label="Sales Rep" />}
            />
            <Button variant="contained" size="small" onClick={handleLink} disabled={busy || !selected || !selected.active}>
              Link
            </Button>
          </Stack>
          <Box>
            <Button
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={busy || !agentName.trim()}
            >
              Create Sales Rep in Sage
            </Button>
          </Box>
        </Stack>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Sales Rep in Sage?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            A new Sales Representative will be created in Sage from this agent and linked to it.
          </Typography>
          <Stack spacing={0.5} data-testid="sales-rep-preview">
            <Typography variant="body2"><strong>First name:</strong> {preview.firstName}</Typography>
            <Typography variant="body2"><strong>Last name:</strong> {preview.lastName || '(none)'}</Typography>
            <Typography variant="body2"><strong>Email:</strong> {primaryEmail || '(none)'}</Typography>
            <Typography variant="body2"><strong>Mobile:</strong> {contactNumber?.trim().slice(0, 30) || '(none)'}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={busy}>Create</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AgentSalesRepLinkPanel;
